import { valueof } from 'src/@types/global'
import {
  DOSSIER_STATUS,
  DOSSIER_STATUS_TYPE,
  FACTSHEET_USER_ROLE_TYPE,
  FACTSHEET_USER_ROLES,
  GLOBAL_USER_ROLES,
  PROCESS_TYPE,
  PROJECT,
  PROJECT_TYPE,
  PROJECT_USER_ROLE_TYPE,
  PROJECT_USER_ROLES,
  USER_ROLES_TYPE,
} from 'src/shared/constants/constants'
import { FACTSHEET_WORKFLOW_STATUS, FACTSHEET_WORKFLOW_STATUS_TYPE } from 'src/shared/constants/factsheet-constants'
import {
  MILESTONE_RESPONSIBLE_TYPE,
  MILESTONE_RESPONSIBLE_TYPE_TYPE,
  MILESTONE_STATUS,
  MILESTONE_STATUS_TYPE,
} from 'src/shared/constants/milestone-constants'
import { Utils } from 'src/shared/utils/Utils'

const hasGFCHEditableRoleForProcess = (process: PROJECT_TYPE, roles: Array<USER_ROLES_TYPE>): boolean => {
  switch (process) {
    case 'PF_KAP': {
      return [GLOBAL_USER_ROLES['PD-GFCH_PF-KAP_CONTRIBUTOR'], GLOBAL_USER_ROLES['PD-GFCH_PF-KAP_COORDINATOR']].some(
        (r) => roles.includes(r),
      )
    }
    case 'PF_PGV': {
      return [GLOBAL_USER_ROLES['PD-GFCH_PF-PGV_CONTRIBUTOR'], GLOBAL_USER_ROLES['PD-GFCH_PF-PGV_COORDINATOR']].some(
        (r) => roles.includes(r),
      )
    }
    case 'KAP': {
      return [GLOBAL_USER_ROLES['PD-GFCH_KAP_CONTRIBUTOR'], GLOBAL_USER_ROLES['PD-GFCH_KAP_COORDINATOR']].some((r) =>
        roles.includes(r),
      )
    }
    default:
      return false
  }
}

const hasGFCHViewRoleForProcess = (process: PROJECT_TYPE, roles: Array<USER_ROLES_TYPE>): boolean => {
  switch (process) {
    case 'PF_KAP': {
      return roles.some((r) => r.startsWith('PD-GFCH_PF-KAP'))
    }
    case 'PF_PGV': {
      return roles.some((r) => r.startsWith('PD-GFCH_PF-PGV'))
    }
    case 'KAP': {
      return roles.some((r) => r.startsWith('PD-GFCH_KAP'))
    }
    default:
      return false
  }
}

const hasExternalViewRoleForProcess = (process: PROJECT_TYPE, roles: Array<USER_ROLES_TYPE>): boolean => {
  switch (process) {
    case 'PF_KAP':
    case 'PF_PGV': {
      return [GLOBAL_USER_ROLES['PD-EXT_USER'], GLOBAL_USER_ROLES['PD-EXT_EXPERT_PROJECT_EVALUATION']].some((role) =>
        roles.includes(role),
      )
    }
    default:
      return false
  }
}

const permissionToCreateProject = (
  process: PROJECT_TYPE,
  globalRoles: Array<USER_ROLES_TYPE>,
  existActiveRounds: boolean,
): boolean => {
  const hasGfchPermission = hasGFCHEditableRoleForProcess(process, globalRoles)
  const hasExternalPermission =
    (hasCantonViewRole(globalRoles) || globalRoles.includes(GLOBAL_USER_ROLES['PD-EXT_USER'])) && existActiveRounds

  return hasGfchPermission || hasExternalPermission
}

const permissionToViewNavLink = (process: PROCESS_TYPE, globalRoles: Array<USER_ROLES_TYPE>): boolean => {
  switch (process) {
    case 'PF_KAP':
    case 'PF_PGV': {
      return (
        hasGFCHViewRoleForProcess(process, globalRoles) ||
        hasCantonViewRole(globalRoles) ||
        hasExternalViewRoleForProcess(process, globalRoles) ||
        hasExternalExpertEvaluationRoleForProcess(process, globalRoles)
      )
    }
    case 'KAP': {
      return hasGFCHViewRoleForProcess(process, globalRoles) || hasCantonViewRole(globalRoles)
    }
    case 'FACTSHEET': {
      // Factsheet is viewable for every user except for users that have only the external expert role for PGV evaluation
      return !globalRoles.every((role) => role === GLOBAL_USER_ROLES['PD-EXT_EVALUATION_TEAM_PGV'])
    }
    default:
      return false
  }
}

const hasGFCHCoordinatorRoleForProcess = (process: PROJECT_TYPE, globalRoles: Array<USER_ROLES_TYPE>) => {
  switch (process) {
    case 'PF_KAP': {
      return globalRoles.includes(GLOBAL_USER_ROLES['PD-GFCH_PF-KAP_COORDINATOR'])
    }
    case 'PF_PGV': {
      return globalRoles.includes(GLOBAL_USER_ROLES['PD-GFCH_PF-PGV_COORDINATOR'])
    }
    case 'KAP': {
      return globalRoles.includes(GLOBAL_USER_ROLES['PD-GFCH_KAP_COORDINATOR'])
    }
    default:
      return false
  }
}

const hasGFCHContributorRoleForProcess = (process: PROJECT_TYPE, globalRoles: Array<USER_ROLES_TYPE>) => {
  switch (process) {
    case 'PF_KAP': {
      return globalRoles.includes(GLOBAL_USER_ROLES['PD-GFCH_PF-KAP_CONTRIBUTOR'])
    }
    case 'PF_PGV': {
      return globalRoles.includes(GLOBAL_USER_ROLES['PD-GFCH_PF-PGV_CONTRIBUTOR'])
    }
    case 'KAP': {
      return globalRoles.includes(GLOBAL_USER_ROLES['PD-GFCH_KAP_CONTRIBUTOR'])
    }
    default:
      return false
  }
}

const hasGFCHReaderRoleForProcess = (process: PROJECT_TYPE, globalRoles: Array<USER_ROLES_TYPE>) => {
  switch (process) {
    case 'PF_KAP': {
      return globalRoles.includes(GLOBAL_USER_ROLES['PD-GFCH_PF-KAP_READER'])
    }
    case 'PF_PGV': {
      return globalRoles.includes(GLOBAL_USER_ROLES['PD-GFCH_PF-PGV_READER'])
    }
    case 'KAP': {
      return globalRoles.includes(GLOBAL_USER_ROLES['PD-GFCH_KAP_READER'])
    }
    default:
      return false
  }
}

const hasExternalAdminRole = (projectUserRoles: Array<valueof<PROJECT_USER_ROLE_TYPE>>): boolean => {
  return projectUserRoles.includes(PROJECT_USER_ROLES.ADMIN)
}

const hasExternalEditableRole = (projectUserRoles: Array<valueof<PROJECT_USER_ROLE_TYPE>>): boolean => {
  return [PROJECT_USER_ROLES.ADMIN, PROJECT_USER_ROLES.CONTRIBUTOR].some((r) => projectUserRoles.includes(r))
}

const hasExternalExpertEvaluationRoleForProcess = (
  process: PROJECT_TYPE,
  globalRoles: Array<USER_ROLES_TYPE>,
): boolean => {
  if (process === PROJECT.PF_PGV) {
    return globalRoles.includes(GLOBAL_USER_ROLES['PD-EXT_EVALUATION_TEAM_PGV'])
  }
  return false
}

const isProjectExpertEvaluationRoleForProcess = (process: PROJECT_TYPE, isProjectExpert: boolean): boolean => {
  return process === PROJECT.PF_PGV && isProjectExpert
}

const hasExternalEditableFactsheetRole = (factsheetUserRoles: Array<valueof<FACTSHEET_USER_ROLE_TYPE>>): boolean => {
  return [FACTSHEET_USER_ROLES.ADMIN, FACTSHEET_USER_ROLES.CONTRIBUTOR].some((r) => factsheetUserRoles.includes(r))
}

const hasCantonEditableRole = (roles: Array<USER_ROLES_TYPE>): boolean => {
  return roles.includes(GLOBAL_USER_ROLES['PD-CANTON_KAP_CONTRIBUTOR'])
}

const hasCantonViewRole = (roles: Array<USER_ROLES_TYPE>): boolean => {
  return roles.some((r) => r.startsWith('PD-CANTON'))
}

/**
 * Project & Program permissions.
 */

const permissionsForProjectImplementation = (
  roles: Array<USER_ROLES_TYPE>,
  status: DOSSIER_STATUS_TYPE | null,
  process: PROJECT_TYPE,
): { canEdit: boolean; canView: boolean; canManageMilestones: boolean } => {
  const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(process, roles)
  const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
  const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)

  switch (status) {
    case 'CONCEPT':
    case 'APPLICATION':
    case 'ACCEPTED':
    case 'REVISION': {
      return {
        canView: hasGFCHRoleForProcess,
        canEdit: hasGFCHEditableRole,
        canManageMilestones: false,
      }
    }

    case 'IMPLEMENTATION': {
      return {
        canView: hasGFCHRoleForProcess,
        canEdit: hasGFCHEditableRole,
        canManageMilestones: hasGFCHCoordinatorRole,
      }
    }

    case 'CANCELED':
    case 'FINISHED':
    case 'REJECTED':
    case 'WITHDRAWN':
    case 'EXCLUDED': {
      return {
        canView: hasGFCHRoleForProcess,
        canEdit: hasGFCHCoordinatorRole,
        canManageMilestones: false,
      }
    }

    default: {
      return { canEdit: false, canView: false, canManageMilestones: false }
    }
  }
}

const canModifyProjectUsers = (
  roles: Array<USER_ROLES_TYPE>,
  process: PROJECT_TYPE,
  projectUserRoles?: Array<valueof<PROJECT_USER_ROLE_TYPE>>,
): boolean => {
  if (projectUserRoles?.includes(PROJECT_USER_ROLES.ADMIN)) {
    return true
  }
  if (process === PROJECT.PF_KAP) {
    return [GLOBAL_USER_ROLES['PD-GFCH_PF-KAP_CONTRIBUTOR'], GLOBAL_USER_ROLES['PD-GFCH_PF-KAP_COORDINATOR']].some(
      (r) => roles.includes(r),
    )
  } else if (process === PROJECT.PF_PGV) {
    return [GLOBAL_USER_ROLES['PD-GFCH_PF-PGV_CONTRIBUTOR'], GLOBAL_USER_ROLES['PD-GFCH_PF-PGV_COORDINATOR']].some(
      (r) => roles.includes(r),
    )
  }
  return false
}

const permissionsForProjectCommittees = (
  roles: Array<USER_ROLES_TYPE>,
  dossier_status: DOSSIER_STATUS_TYPE | null,
  process: PROJECT_TYPE,
): { canEdit: boolean; canView: boolean } => {
  switch (dossier_status) {
    case 'CONCEPT':
    case 'APPLICATION':
    case 'ACCEPTED':
    case 'REVISION':
    case 'IMPLEMENTATION': {
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(process, roles)
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)

      return {
        canView: hasGFCHRoleForProcess,
        canEdit: hasGFCHEditableRole,
      }
    }

    case 'CANCELED':
    case 'FINISHED':
    case 'REJECTED':
    case 'WITHDRAWN':
    case 'EXCLUDED': {
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)

      return {
        canView: hasGFCHRoleForProcess,
        canEdit: hasGFCHCoordinatorRole,
      }
    }

    default: {
      return { canEdit: false, canView: false }
    }
  }
}

const permissionsForProjectAssessment = (
  roles: Array<USER_ROLES_TYPE>,
  projectUserRoles: Array<valueof<PROJECT_USER_ROLE_TYPE>>,
  dossier_status: DOSSIER_STATUS_TYPE | null,
  process: PROJECT_TYPE,
  userIsAssessor: boolean,
): { canEdit: boolean; canView: boolean; canAdd: boolean; canDelete: boolean } => {
  switch (dossier_status) {
    case 'CONCEPT':
    case 'APPLICATION':
    case 'IMPLEMENTATION':
    case 'ACCEPTED':
    case 'REVISION': {
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)
      const hasGFCHContributorRole = hasGFCHContributorRoleForProcess(process, roles)
      const hasGFCHReaderRole = hasGFCHReaderRoleForProcess(process, roles)

      return {
        canView: hasGFCHRoleForProcess || projectUserRoles.includes(PROJECT_USER_ROLES['EXPERT']),
        canEdit:
          hasGFCHCoordinatorRole ||
          projectUserRoles.includes(PROJECT_USER_ROLES['EXPERT']) ||
          ((hasGFCHContributorRole || hasGFCHReaderRole) && userIsAssessor),
        canAdd: hasGFCHCoordinatorRole,
        canDelete: hasGFCHCoordinatorRole,
      }
    }

    case 'CANCELED':
    case 'FINISHED':
    case 'REJECTED':
    case 'WITHDRAWN':
    case 'EXCLUDED': {
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)

      return {
        canView: hasGFCHRoleForProcess || projectUserRoles.includes(PROJECT_USER_ROLES['EXPERT']),
        canEdit: hasGFCHCoordinatorRole,
        canAdd: hasGFCHCoordinatorRole,
        canDelete: hasGFCHCoordinatorRole,
      }
    }

    default: {
      return { canEdit: false, canView: false, canAdd: false, canDelete: false }
    }
  }
}

// Permissions for application: https://plaza.netcetera.com/wiki/display/GFS0086/Permissions+V2
const permissionsForProjectApplication = (
  roles: Array<USER_ROLES_TYPE>,
  projectUserRoles: Array<valueof<PROJECT_USER_ROLE_TYPE>>,
  userIsProjectExpert: boolean,
  status: DOSSIER_STATUS_TYPE | null,
  process: PROJECT_TYPE,
): { canEdit: boolean; canView: boolean; canViewSummaryTab: boolean } => {
  switch (status) {
    case 'CONCEPT':
    case 'APPLICATION':
    case 'ACCEPTED':
    case 'REVISION': {
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(process, roles)
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)

      return {
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEdit: hasGFCHEditableRole,
        canViewSummaryTab: hasGFCHRoleForProcess,
      }
    }

    case 'IMPLEMENTATION': {
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(process, roles)
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)

      return {
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEdit: hasGFCHEditableRole,
        canViewSummaryTab: hasGFCHRoleForProcess,
      }
    }

    case 'CANCELED':
    case 'FINISHED': {
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)

      return {
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEdit: hasGFCHCoordinatorRole,
        canViewSummaryTab: hasGFCHRoleForProcess,
      }
    }
    case 'REJECTED':
    case 'WITHDRAWN':
    case 'EXCLUDED': {
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)

      return {
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEdit: hasGFCHCoordinatorRole,
        canViewSummaryTab: hasGFCHRoleForProcess,
      }
    }

    default: {
      return { canEdit: false, canView: false, canViewSummaryTab: false }
    }
  }
}

const permissionsForProjectDescription = (
  roles: Array<USER_ROLES_TYPE>,
  projectUserRoles: Array<valueof<PROJECT_USER_ROLE_TYPE>>,
  userIsProjectExpert: boolean,
  status: DOSSIER_STATUS_TYPE,
  process: PROJECT_TYPE,
  existsActiveFundingRound = false,
): {
  canEdit: boolean
  canView: boolean
  canEditProjectBudgetApproved: boolean
  canEditCommentInSummary: boolean
  canModifyProjectUsers: boolean
  canModifyProjectExperts: boolean
  canTransitToApplication: boolean
  canDeleteProject: boolean
  canModifyGoals: boolean
  canEditContractDates: boolean
  canModifyEffectivityManagement: boolean
} => {
  switch (status) {
    case 'CONCEPT': {
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)

      const hasPerProjectEditableRole = hasExternalEditableRole(projectUserRoles)
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(process, roles)
      const isProjectLeader = projectUserRoles?.includes(PROJECT_USER_ROLES.ADMIN)
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasPermissionToModifyProjectUsers = canModifyProjectUsers(roles, process, projectUserRoles)
      const hasPermissionToModifyProjectExperts = hasGFCHCoordinatorRoleForProcess(process, roles)

      return {
        canEdit: hasPerProjectEditableRole || hasGFCHEditableRole,
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEditProjectBudgetApproved: hasGFCHEditableRole,
        canEditCommentInSummary: hasPerProjectEditableRole || hasGFCHEditableRole,
        canModifyProjectUsers: hasPermissionToModifyProjectUsers,
        canModifyProjectExperts: hasPermissionToModifyProjectExperts,
        canTransitToApplication: hasGFCHEditableRole || (existsActiveFundingRound && hasPerProjectEditableRole),
        canDeleteProject: hasGFCHCoordinatorRole || isProjectLeader,
        canModifyGoals: hasPerProjectEditableRole || hasGFCHEditableRole,
        canModifyEffectivityManagement: hasPerProjectEditableRole || hasGFCHEditableRole,
        canEditContractDates: hasGFCHEditableRole,
      }
    }
    case 'REVISION': {
      const hasPerProjectEditableRole = hasExternalEditableRole(projectUserRoles)
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(process, roles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasPermissionToModifyProjectUsers = canModifyProjectUsers(roles, process, projectUserRoles)
      const hasPermissionToModifyProjectExperts = hasGFCHCoordinatorRoleForProcess(process, roles)

      return {
        canEdit: hasPerProjectEditableRole || hasGFCHEditableRole,
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEditProjectBudgetApproved: hasGFCHEditableRole,
        canEditCommentInSummary: hasPerProjectEditableRole || hasGFCHEditableRole,
        canModifyProjectUsers: hasPermissionToModifyProjectUsers,
        canModifyProjectExperts: hasPermissionToModifyProjectExperts,
        canTransitToApplication: hasGFCHCoordinatorRole || hasPerProjectEditableRole,
        canDeleteProject: false,
        canModifyGoals: hasPerProjectEditableRole || hasGFCHEditableRole,
        canModifyEffectivityManagement: hasPerProjectEditableRole || hasGFCHEditableRole,
        canEditContractDates: hasGFCHEditableRole,
      }
    }
    case 'APPLICATION': {
      const hasGlobalRole = hasGFCHEditableRoleForProcess(process, roles)
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasAdminOrContributorProjectRole = hasExternalEditableRole(projectUserRoles)
      const hasPermissionToModifyProjectUsers = canModifyProjectUsers(roles, process, projectUserRoles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)
      const hasPermissionToModifyProjectExperts = hasGFCHCoordinatorRoleForProcess(process, roles)
      return {
        canEdit: hasGlobalRole,
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEditProjectBudgetApproved: hasGFCHCoordinatorRole,
        canEditCommentInSummary: hasAdminOrContributorProjectRole || hasGlobalRole,
        canModifyProjectUsers: hasPermissionToModifyProjectUsers,
        canModifyProjectExperts: hasPermissionToModifyProjectExperts,
        canTransitToApplication: false,
        canDeleteProject: false,
        canModifyGoals: hasGlobalRole,
        canEditContractDates: hasGlobalRole,
        canModifyEffectivityManagement: hasGlobalRole,
      }
    }
    case 'IMPLEMENTATION': {
      const hasGlobalRole = hasGFCHEditableRoleForProcess(process, roles)
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasAdminOrContributorProjectRole = hasExternalEditableRole(projectUserRoles)
      const hasPermissionToModifyProjectUsers = canModifyProjectUsers(roles, process, projectUserRoles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)
      const hasPermissionToModifyProjectExperts = hasGFCHCoordinatorRoleForProcess(process, roles)

      return {
        canEdit: hasGlobalRole,
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEditProjectBudgetApproved: hasGFCHCoordinatorRole,
        canEditCommentInSummary: hasAdminOrContributorProjectRole || hasGlobalRole,
        canModifyProjectUsers: hasPermissionToModifyProjectUsers,
        canModifyProjectExperts: hasPermissionToModifyProjectExperts,
        canTransitToApplication: false,
        canDeleteProject: false,
        canModifyGoals: hasGFCHCoordinatorRole,
        canEditContractDates: hasGlobalRole,
        canModifyEffectivityManagement:
          hasGlobalRole || isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
      }
    }
    case 'ACCEPTED': {
      const hasGlobalRole = hasGFCHEditableRoleForProcess(process, roles)
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasAdminOrContributorProjectRole = hasExternalEditableRole(projectUserRoles)
      const hasPermissionToModifyProjectUsers = canModifyProjectUsers(roles, process, projectUserRoles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)
      const hasPermissionToModifyProjectExperts = hasGFCHCoordinatorRoleForProcess(process, roles)
      return {
        canEdit: hasGlobalRole,
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEditProjectBudgetApproved: hasGFCHCoordinatorRole,
        canEditCommentInSummary: hasAdminOrContributorProjectRole || hasGlobalRole,
        canModifyProjectUsers: hasPermissionToModifyProjectUsers,
        canModifyProjectExperts: hasPermissionToModifyProjectExperts,
        canTransitToApplication: false,
        canDeleteProject: false,
        canModifyGoals: hasGlobalRole,
        canEditContractDates: hasGlobalRole,
        canModifyEffectivityManagement: hasGlobalRole,
      }
    }
    case 'CANCELED':
    case 'FINISHED': {
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)
      const hasGlobalRole = hasGFCHEditableRoleForProcess(process, roles)
      const hasPermissionToModifyProjectExperts = hasGFCHCoordinatorRoleForProcess(process, roles)

      return {
        canEdit: hasGFCHCoordinatorRole,
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEditProjectBudgetApproved: hasGFCHCoordinatorRole,
        canEditCommentInSummary: hasGFCHCoordinatorRole,
        canModifyProjectUsers: hasGFCHCoordinatorRole,
        canModifyProjectExperts: hasPermissionToModifyProjectExperts,
        canTransitToApplication: false,
        canDeleteProject: false,
        canModifyGoals: hasGFCHCoordinatorRole,
        canEditContractDates: hasGlobalRole,
        canModifyEffectivityManagement:
          hasGFCHCoordinatorRole || isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
      }
    }
    case 'REJECTED':
    case 'WITHDRAWN':
    case 'EXCLUDED': {
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)
      const hasGlobalRole = hasGFCHEditableRoleForProcess(process, roles)
      const hasPermissionToModifyProjectExperts = hasGFCHCoordinatorRoleForProcess(process, roles)

      return {
        canEdit: hasGFCHCoordinatorRole,
        canView:
          hasAnyRoleOnTheProject ||
          hasGFCHRoleForProcess ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEditProjectBudgetApproved: hasGFCHCoordinatorRole,
        canEditCommentInSummary: hasGFCHCoordinatorRole,
        canModifyProjectUsers: hasGFCHCoordinatorRole,
        canModifyProjectExperts: hasPermissionToModifyProjectExperts,
        canTransitToApplication: false,
        canDeleteProject: false,
        canModifyGoals: hasGFCHCoordinatorRole,
        canEditContractDates: hasGlobalRole,
        canModifyEffectivityManagement: hasGFCHCoordinatorRole,
      }
    }

    default:
      return {
        canEdit: false,
        canView: false,
        canEditProjectBudgetApproved: false,
        canEditCommentInSummary: false,
        canModifyProjectUsers: false,
        canModifyProjectExperts: false,
        canTransitToApplication: false,
        canDeleteProject: false,
        canModifyGoals: false,
        canEditContractDates: false,
        canModifyEffectivityManagement: false,
      }
  }
}

const permissionToEditProjectMilestoneConditions = (
  process: PROJECT_TYPE,
  globalRoles: Array<USER_ROLES_TYPE>,
  status: DOSSIER_STATUS_TYPE,
  milestoneStatus: MILESTONE_STATUS_TYPE,
): boolean => {
  switch (status) {
    case DOSSIER_STATUS.IMPLEMENTATION: {
      if (milestoneStatus === MILESTONE_STATUS.DONE) {
        return hasGFCHCoordinatorRoleForProcess(process, globalRoles)
      } else return hasGFCHEditableRoleForProcess(process, globalRoles)
    }
    case DOSSIER_STATUS.CANCELED:
    case DOSSIER_STATUS.FINISHED: {
      return hasGFCHCoordinatorRoleForProcess(process, globalRoles)
    }
    default:
      return false
  }
}

const permissionsForKapProgramDescription = (
  roles: Array<USER_ROLES_TYPE>,
  status: DOSSIER_STATUS_TYPE,
  isOwnCanton: boolean,
): {
  canEdit: boolean
  canEditMeasures: boolean
  canEditApplication: boolean
  canCopyMeasures: boolean
  canViewCantonRestricted: boolean
  canView: boolean
  canManageMilestones: boolean
  canDeleteProgram: boolean
  canModifyGoals: boolean
} => {
  const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(PROJECT.KAP, roles)

  switch (status) {
    case 'CONCEPT':
    case 'CONCEPT_REWORK': {
      const hasPerProgramEditableRole = hasCantonEditableRole(roles)
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(PROJECT.KAP, roles)
      const hasPerProgramViewRole = hasCantonViewRole(roles)
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(PROJECT.KAP, roles)

      return {
        canEdit: (isOwnCanton && hasPerProgramEditableRole) || hasGFCHEditableRole,
        canEditMeasures: (isOwnCanton && hasPerProgramEditableRole) || hasGFCHEditableRole,
        canEditApplication: hasGFCHEditableRole,
        canCopyMeasures: (isOwnCanton && hasPerProgramEditableRole) || hasGFCHEditableRole,
        canView: (isOwnCanton && hasPerProgramViewRole) || hasGFCHRoleForProcess,
        canViewCantonRestricted: (isOwnCanton && hasPerProgramViewRole) || hasGFCHRoleForProcess,
        canManageMilestones: false,
        canDeleteProgram: hasGFCHCoordinatorRole,
        canModifyGoals: (isOwnCanton && hasPerProgramEditableRole) || hasGFCHEditableRole,
      }
    }

    case 'CONCEPT_REVIEW': {
      const hasPerProgramEditableRole = hasCantonEditableRole(roles)
      const hasPerProgramViewRole = hasCantonViewRole(roles)
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(PROJECT.KAP, roles)
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(PROJECT.KAP, roles)

      return {
        canEdit: hasGFCHEditableRole,
        canEditMeasures: (isOwnCanton && hasPerProgramEditableRole) || hasGFCHEditableRole,
        canEditApplication: hasGFCHEditableRole,
        canCopyMeasures: hasGFCHEditableRole,
        canView: (isOwnCanton && hasPerProgramViewRole) || hasGFCHRoleForProcess,
        canViewCantonRestricted: (isOwnCanton && hasPerProgramViewRole) || hasGFCHRoleForProcess,
        canManageMilestones: false,
        canDeleteProgram: hasGFCHCoordinatorRole,
        canModifyGoals: hasGFCHEditableRole,
      }
    }
    case 'APPLICATION': {
      const hasPerProgramViewRole = hasCantonViewRole(roles)
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(PROJECT.KAP, roles)
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(PROJECT.KAP, roles)

      return {
        canEdit: hasGFCHEditableRole,
        canEditMeasures: hasGFCHEditableRole,
        canEditApplication: hasGFCHEditableRole,
        canCopyMeasures: false,
        canView: (isOwnCanton && hasPerProgramViewRole) || hasGFCHRoleForProcess,
        canViewCantonRestricted: (isOwnCanton && hasPerProgramViewRole) || hasGFCHRoleForProcess,
        canManageMilestones: hasGFCHCoordinatorRole,
        canDeleteProgram: hasGFCHCoordinatorRole,
        canModifyGoals: hasGFCHEditableRole,
      }
    }

    case 'IMPLEMENTATION': {
      const hasPerProgramEditableRole = hasCantonEditableRole(roles)
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(PROJECT.KAP, roles)
      const hasPerProgramViewRole = hasCantonViewRole(roles)
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(PROJECT.KAP, roles)

      return {
        canEdit: hasGFCHEditableRole,
        canEditMeasures: (isOwnCanton && hasPerProgramEditableRole) || hasGFCHEditableRole,
        canEditApplication: hasGFCHEditableRole,
        canCopyMeasures: false,
        canView: hasPerProgramViewRole || hasGFCHRoleForProcess,
        canViewCantonRestricted: (isOwnCanton && hasPerProgramViewRole) || hasGFCHRoleForProcess,
        canManageMilestones: hasGFCHCoordinatorRole,
        canDeleteProgram: hasGFCHCoordinatorRole,
        canModifyGoals: hasGFCHCoordinatorRole,
      }
    }

    case 'CANCELED': {
      const hasPerProgramViewRole = hasCantonViewRole(roles)
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(PROJECT.KAP, roles)

      return {
        canEdit: hasGFCHCoordinatorRole,
        canEditMeasures: hasGFCHCoordinatorRole,
        canEditApplication: hasGFCHCoordinatorRole,
        canCopyMeasures: false,
        canView: (isOwnCanton && hasPerProgramViewRole) || hasGFCHRoleForProcess,
        canViewCantonRestricted: (isOwnCanton && hasPerProgramViewRole) || hasGFCHRoleForProcess,
        canManageMilestones: false,
        canDeleteProgram: hasGFCHCoordinatorRole,
        canModifyGoals: hasGFCHCoordinatorRole,
      }
    }

    case 'FINISHED': {
      const hasPerProgramViewRole = hasCantonViewRole(roles)
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(PROJECT.KAP, roles)

      return {
        canEdit: hasGFCHCoordinatorRole,
        canEditMeasures: hasGFCHCoordinatorRole,
        canEditApplication: hasGFCHCoordinatorRole,
        canCopyMeasures: false,
        canView: hasPerProgramViewRole || hasGFCHRoleForProcess,
        canViewCantonRestricted: (isOwnCanton && hasPerProgramViewRole) || hasGFCHRoleForProcess,
        canManageMilestones: false,
        canDeleteProgram: hasGFCHCoordinatorRole,
        canModifyGoals: hasGFCHCoordinatorRole,
      }
    }

    default:
      return {
        canEdit: false,
        canEditMeasures: false,
        canEditApplication: false,
        canCopyMeasures: false,
        canView: false,
        canViewCantonRestricted: false,
        canManageMilestones: false,
        canDeleteProgram: false,
        canModifyGoals: false,
      }
  }
}

const permissionToCreateKapProgram = (globalRoles: Array<USER_ROLES_TYPE>): boolean => {
  return hasGFCHEditableRoleForProcess(PROJECT.KAP, globalRoles) || hasCantonEditableRole(globalRoles)
}

/**
 * Administrative permissions.
 */
const permissionToAdministrateUsers = (
  roles: Array<USER_ROLES_TYPE>,
): {
  canView: boolean
  canEdit: boolean
} => {
  const allowedGlobalRolesToViewAndEdit = [
    GLOBAL_USER_ROLES['PD-GFCH_PF-KAP_COORDINATOR'],
    GLOBAL_USER_ROLES['PD-GFCH_PF-PGV_COORDINATOR'],
    GLOBAL_USER_ROLES['PD-GFCH_KAP_COORDINATOR'],
  ]

  const canViewAndEdit = allowedGlobalRolesToViewAndEdit.some((role) => roles.includes(GLOBAL_USER_ROLES[role]))
  return {
    canView: canViewAndEdit,
    canEdit: canViewAndEdit,
  }
}

const permissionToAdministrateFundingRounds = (
  roles: Array<USER_ROLES_TYPE>,
): {
  canView: boolean
  canEdit: boolean
} => {
  const allowedGlobalRolesToView = [
    GLOBAL_USER_ROLES['PD-GFCH_PF-KAP_COORDINATOR'],
    GLOBAL_USER_ROLES['PD-GFCH_PF-PGV_COORDINATOR'],
    GLOBAL_USER_ROLES['PD-GFCH_KAP_COORDINATOR'],
    GLOBAL_USER_ROLES['PD-GFCH_FACTSHEET_COORDINATOR'],
  ]
  const allowedGlobalRolesToEdit = [
    GLOBAL_USER_ROLES['PD-GFCH_PF-KAP_COORDINATOR'],
    GLOBAL_USER_ROLES['PD-GFCH_PF-PGV_COORDINATOR'],
    GLOBAL_USER_ROLES['PD-GFCH_KAP_COORDINATOR'],
    GLOBAL_USER_ROLES['PD-GFCH_FACTSHEET_COORDINATOR'],
  ]

  const canView = allowedGlobalRolesToView.some((role) => roles.includes(GLOBAL_USER_ROLES[role]))
  const canEdit = allowedGlobalRolesToEdit.some((role) => roles.includes(GLOBAL_USER_ROLES[role]))

  return {
    canView: canView,
    canEdit: canEdit,
  }
}

const permissionToViewAdministrationLink = (globalRoles: Array<USER_ROLES_TYPE>): boolean => {
  const { canView: canViewUserAdministration } = permissionToAdministrateUsers(globalRoles)
  const { canView: canViewRoundManagement } = permissionToAdministrateFundingRounds(globalRoles)

  return canViewUserAdministration || canViewRoundManagement
}

/**
 * Factsheet permissions.
 */

const permissionToFactsheets = (
  roles: Array<USER_ROLES_TYPE>,
): {
  canCreate: boolean
  canViewAllFactsheets: boolean
  canDownloadReport: boolean
  canCopy: boolean
} => {
  const isFactsheetCoordinator = roles.includes(GLOBAL_USER_ROLES['PD-GFCH_FACTSHEET_COORDINATOR'])
  const isPfkapContributor = hasGFCHContributorRoleForProcess(PROJECT.PF_KAP, roles)

  const canCreate = isFactsheetCoordinator

  const canViewAllFactsheets = isFactsheetCoordinator || isPfkapContributor
  const canDownloadReport = Utils.isInternalRole(roles)
  const canCopy = isFactsheetCoordinator

  return {
    canCreate,
    canViewAllFactsheets,
    canDownloadReport,
    canCopy,
  }
}

const permissionToFactsheetDetails = (
  roles: Array<USER_ROLES_TYPE>,
  factsheetUserRoles: Array<valueof<FACTSHEET_USER_ROLE_TYPE>>,
  status: FACTSHEET_WORKFLOW_STATUS_TYPE | null,
  published: boolean,
): {
  canEdit: boolean
  canView: boolean
  canDelete: boolean
  canViewClassificationData: boolean
  canEditClassificationData: boolean
} => {
  const isFactsheetCoordinator = roles.includes(GLOBAL_USER_ROLES['PD-GFCH_FACTSHEET_COORDINATOR'])

  const isPfkapContributor = hasGFCHContributorRoleForProcess(PROJECT.PF_KAP, roles)

  const isKapContributor = hasGFCHContributorRoleForProcess(PROJECT.KAP, roles)

  const canBeEditedByExternalUser = hasExternalEditableFactsheetRole(factsheetUserRoles)

  const canBeViewedByExternalUser = factsheetUserRoles.length > 0

  const publishedAndCanBeViewedByExternalUser = published && canBeViewedByExternalUser

  if (isFactsheetCoordinator) {
    return {
      canView: true,
      canEdit: true,
      canDelete: true,
      canViewClassificationData: true,
      canEditClassificationData: true,
    }
  }

  switch (status) {
    case FACTSHEET_WORKFLOW_STATUS.CONCEPT: {
      return {
        canView: canBeViewedByExternalUser || isPfkapContributor || isKapContributor,
        canEdit: canBeEditedByExternalUser,
        canDelete: false,
        canViewClassificationData:
          isFactsheetCoordinator || isPfkapContributor || publishedAndCanBeViewedByExternalUser,
        canEditClassificationData: isFactsheetCoordinator || isPfkapContributor,
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.APPLICATION: {
      return {
        canView: canBeViewedByExternalUser || isPfkapContributor || isKapContributor,
        canEdit: isPfkapContributor,
        canDelete: false,
        canViewClassificationData: isFactsheetCoordinator || isPfkapContributor || canBeViewedByExternalUser,
        canEditClassificationData: isFactsheetCoordinator || isPfkapContributor,
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.REVISION: {
      return {
        canView: canBeViewedByExternalUser || isPfkapContributor || isKapContributor,
        canEdit: canBeEditedByExternalUser,
        canDelete: false,
        canViewClassificationData: isFactsheetCoordinator || isPfkapContributor || canBeViewedByExternalUser,
        canEditClassificationData: isFactsheetCoordinator || isPfkapContributor,
      }
    }

    // handle published and not published
    case FACTSHEET_WORKFLOW_STATUS.ACCEPTED: {
      if (published) {
        return {
          canView: true,
          canEdit: false,
          canDelete: false,
          canViewClassificationData: isFactsheetCoordinator || isPfkapContributor || canBeViewedByExternalUser,
          canEditClassificationData: isFactsheetCoordinator || isPfkapContributor,
        }
      } else {
        return {
          canView: canBeViewedByExternalUser || isPfkapContributor || isKapContributor,
          canEdit: false,
          canDelete: false,
          canViewClassificationData: isFactsheetCoordinator || isPfkapContributor || canBeViewedByExternalUser,
          canEditClassificationData: isFactsheetCoordinator || isPfkapContributor,
        }
      }
    }

    // handle published and not published
    case FACTSHEET_WORKFLOW_STATUS.CANCELED:
    case FACTSHEET_WORKFLOW_STATUS.REJECTED:
    case FACTSHEET_WORKFLOW_STATUS.EXCLUDED:
    case FACTSHEET_WORKFLOW_STATUS.WITHDRAWN: {
      return {
        canView: canBeViewedByExternalUser || isPfkapContributor || isKapContributor,
        canEdit: false,
        canDelete: false,
        canViewClassificationData: isFactsheetCoordinator || isPfkapContributor || canBeViewedByExternalUser,
        canEditClassificationData: isFactsheetCoordinator || isPfkapContributor,
      }
    }

    default:
      return {
        canEdit: false,
        canView: false,
        canDelete: false,
        canViewClassificationData: isFactsheetCoordinator || isPfkapContributor || canBeViewedByExternalUser,
        canEditClassificationData: isFactsheetCoordinator || isPfkapContributor,
      }
  }
}

const permissionToFactsheetDetailsSummary = (
  roles: Array<USER_ROLES_TYPE>,
  factsheetUserRoles: Array<valueof<FACTSHEET_USER_ROLE_TYPE>>,
  status: FACTSHEET_WORKFLOW_STATUS_TYPE | null,
  published: boolean,
  isProjectLead: boolean,
): { canView: boolean; canDelete: boolean; canEdit: boolean } => {
  const isFactsheetCoordinator = roles.includes(GLOBAL_USER_ROLES['PD-GFCH_FACTSHEET_COORDINATOR'])

  const canBeViewedByPfkapContributor = hasGFCHContributorRoleForProcess(PROJECT.PF_KAP, roles)

  const canBeViewedByExternalUser = factsheetUserRoles.length > 0

  if (isFactsheetCoordinator) {
    return {
      canView: true,
      canDelete: true,
      canEdit: true,
    }
  }

  switch (status) {
    case FACTSHEET_WORKFLOW_STATUS.CONCEPT: {
      return {
        canView: canBeViewedByExternalUser || canBeViewedByPfkapContributor,
        canDelete: false,
        canEdit: isProjectLead || canBeViewedByPfkapContributor,
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.APPLICATION: {
      return {
        canView: canBeViewedByExternalUser || canBeViewedByPfkapContributor,
        canDelete: false,
        canEdit: canBeViewedByPfkapContributor,
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.REVISION: {
      return {
        canView: canBeViewedByExternalUser || canBeViewedByPfkapContributor,
        canDelete: false,
        canEdit: isProjectLead || canBeViewedByPfkapContributor,
      }
    }

    // handle published and not published
    case FACTSHEET_WORKFLOW_STATUS.ACCEPTED: {
      return {
        canView: canBeViewedByExternalUser || canBeViewedByPfkapContributor,
        canDelete: false,
        canEdit: false,
      }
    }

    // handle published and not published
    case FACTSHEET_WORKFLOW_STATUS.CANCELED:
    case FACTSHEET_WORKFLOW_STATUS.REJECTED:
    case FACTSHEET_WORKFLOW_STATUS.EXCLUDED:
    case FACTSHEET_WORKFLOW_STATUS.WITHDRAWN: {
      return {
        canView: canBeViewedByExternalUser || canBeViewedByPfkapContributor,
        canDelete: false,
        canEdit: false,
      }
    }

    default:
      return {
        canView: false,
        canDelete: false,
        canEdit: false,
      }
  }
}

const permissionToFactsheetRelatedProjects = (
  roles: Array<USER_ROLES_TYPE>,
  factsheetUserRoles: Array<valueof<FACTSHEET_USER_ROLE_TYPE>>,
  status: FACTSHEET_WORKFLOW_STATUS_TYPE | null,
  published: boolean,
): {
  canDelete: boolean
  canViewLinkedMeasures: boolean
  canViewLinkedProjects: boolean
  canClickOnLinkedProjects: boolean
  canClickOnLinkedMeasures: boolean
} => {
  const isFactsheetCoordinator = roles.includes(GLOBAL_USER_ROLES['PD-GFCH_FACTSHEET_COORDINATOR'])

  const canBeViewedByPfkapContributor = hasGFCHContributorRoleForProcess(PROJECT.PF_KAP, roles)

  const canBeViewedByKapContributor = hasGFCHContributorRoleForProcess(PROJECT.KAP, roles)

  const canBeViewedByCantonalUser = published && hasCantonViewRole(roles)

  const canBeViewedByExternalUser = factsheetUserRoles.length > 0

  if (isFactsheetCoordinator) {
    return {
      canDelete: true,
      canViewLinkedProjects: true,
      canViewLinkedMeasures: true,
      canClickOnLinkedMeasures: true,
      canClickOnLinkedProjects: true,
    }
  }

  switch (status) {
    case FACTSHEET_WORKFLOW_STATUS.CONCEPT: {
      return {
        canViewLinkedProjects:
          canBeViewedByExternalUser || canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canViewLinkedMeasures: canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canClickOnLinkedMeasures: canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canClickOnLinkedProjects: canBeViewedByPfkapContributor,
        canDelete: false,
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.APPLICATION: {
      return {
        canViewLinkedProjects:
          canBeViewedByExternalUser || canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canViewLinkedMeasures: canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canClickOnLinkedMeasures: canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canClickOnLinkedProjects: canBeViewedByPfkapContributor,
        canDelete: false,
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.REVISION: {
      return {
        canViewLinkedProjects:
          canBeViewedByExternalUser || canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canViewLinkedMeasures: canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canClickOnLinkedMeasures: canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canClickOnLinkedProjects: canBeViewedByPfkapContributor,
        canDelete: false,
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.ACCEPTED: {
      return {
        canViewLinkedProjects:
          canBeViewedByExternalUser || canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canViewLinkedMeasures:
          canBeViewedByPfkapContributor || canBeViewedByCantonalUser || canBeViewedByKapContributor,
        canClickOnLinkedMeasures:
          canBeViewedByPfkapContributor || canBeViewedByCantonalUser || canBeViewedByKapContributor,
        canClickOnLinkedProjects: canBeViewedByPfkapContributor,
        canDelete: false,
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.CANCELED:
    case FACTSHEET_WORKFLOW_STATUS.REJECTED:
    case FACTSHEET_WORKFLOW_STATUS.EXCLUDED:
    case FACTSHEET_WORKFLOW_STATUS.WITHDRAWN: {
      return {
        canViewLinkedProjects:
          canBeViewedByExternalUser || canBeViewedByPfkapContributor || canBeViewedByKapContributor,
        canViewLinkedMeasures:
          canBeViewedByPfkapContributor || canBeViewedByCantonalUser || canBeViewedByKapContributor,
        canClickOnLinkedMeasures:
          canBeViewedByPfkapContributor || canBeViewedByCantonalUser || canBeViewedByKapContributor,
        canClickOnLinkedProjects: canBeViewedByPfkapContributor,
        canDelete: false,
      }
    }

    default:
      return {
        canDelete: false,
        canViewLinkedProjects: false,
        canViewLinkedMeasures: false,
        canClickOnLinkedMeasures: false,
        canClickOnLinkedProjects: false,
      }
  }
}

const permissionsForFactsheetUsers = (
  roles: Array<USER_ROLES_TYPE>,
  factsheetUserRoles: Array<valueof<FACTSHEET_USER_ROLE_TYPE>>,
  status: FACTSHEET_WORKFLOW_STATUS_TYPE,
): { canModifyFactsheetUsers: boolean; canView: boolean } => {
  const isFactsheetCoordinator = roles.includes(GLOBAL_USER_ROLES['PD-GFCH_FACTSHEET_COORDINATOR'])

  const isPfkapContributor = hasGFCHContributorRoleForProcess(PROJECT.PF_KAP, roles)

  // project lead
  const isExternalAdmin = factsheetUserRoles.includes(FACTSHEET_USER_ROLES['ADMIN'])

  const canBeViewedByExternalUser = factsheetUserRoles.length > 0

  // has all permissions
  if (isFactsheetCoordinator) {
    return {
      canModifyFactsheetUsers: true,
      canView: true,
    }
  }

  switch (status) {
    case FACTSHEET_WORKFLOW_STATUS.CONCEPT:
      return {
        canView: canBeViewedByExternalUser || isPfkapContributor,
        canModifyFactsheetUsers: isExternalAdmin || isPfkapContributor,
      }
    case FACTSHEET_WORKFLOW_STATUS.APPLICATION:
      return {
        canView: canBeViewedByExternalUser || isPfkapContributor,
        canModifyFactsheetUsers: false,
      }
    case FACTSHEET_WORKFLOW_STATUS.REVISION:
      return {
        canView: canBeViewedByExternalUser || isPfkapContributor,
        canModifyFactsheetUsers: isExternalAdmin || isPfkapContributor,
      }

    case FACTSHEET_WORKFLOW_STATUS.ACCEPTED: {
      return {
        canView: canBeViewedByExternalUser || isPfkapContributor,
        canModifyFactsheetUsers: false,
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.CANCELED:
    case FACTSHEET_WORKFLOW_STATUS.REJECTED:
    case FACTSHEET_WORKFLOW_STATUS.EXCLUDED:
    case FACTSHEET_WORKFLOW_STATUS.WITHDRAWN:
      return {
        canView: canBeViewedByExternalUser || isPfkapContributor,
        canModifyFactsheetUsers: false,
      }

    default:
      return {
        canView: false,
        canModifyFactsheetUsers: false,
      }
  }
}

const permissionsForFactsheetApplicationDetails = (
  roles: Array<USER_ROLES_TYPE>,
  status: FACTSHEET_WORKFLOW_STATUS_TYPE,
  published: boolean,
): { canView: boolean; canEdit: boolean } => {
  const isFactsheetCoordinator = roles.includes(GLOBAL_USER_ROLES['PD-GFCH_FACTSHEET_COORDINATOR'])
  const isPfkapContributor = hasGFCHContributorRoleForProcess(PROJECT.PF_KAP, roles)

  if (isFactsheetCoordinator) {
    return {
      canEdit: true,
      canView: true,
    }
  }

  switch (status) {
    case FACTSHEET_WORKFLOW_STATUS.APPLICATION:
    case FACTSHEET_WORKFLOW_STATUS.REVISION:
    case FACTSHEET_WORKFLOW_STATUS.CONCEPT: {
      return {
        canView: isPfkapContributor,
        canEdit: false,
      }
    }

    // handle published and not published
    case FACTSHEET_WORKFLOW_STATUS.ACCEPTED: {
      if (published) {
        return {
          canView: isPfkapContributor,
          canEdit: false,
        }
      } else {
        return {
          canView: false,
          canEdit: false,
        }
      }
    }

    // handle not published
    case FACTSHEET_WORKFLOW_STATUS.CANCELED:
    case FACTSHEET_WORKFLOW_STATUS.WITHDRAWN: {
      return {
        canView: false,
        canEdit: false,
      }
    }

    default:
      return {
        canView: false,
        canEdit: false,
      }
  }
}

const permissionsForFactsheetCommittees = (
  roles: Array<USER_ROLES_TYPE>,
  status: FACTSHEET_WORKFLOW_STATUS_TYPE,
  published: boolean,
): {
  canEdit: boolean
  canView: boolean
} => {
  const isFactsheetCoordinator = roles.includes(GLOBAL_USER_ROLES['PD-GFCH_FACTSHEET_COORDINATOR'])
  const isPfkapContributor = hasGFCHContributorRoleForProcess(PROJECT.PF_KAP, roles)

  if (isFactsheetCoordinator) {
    return {
      canEdit: true,
      canView: true,
    }
  }

  switch (status) {
    case FACTSHEET_WORKFLOW_STATUS.CONCEPT:
      return {
        canView: isPfkapContributor,
        canEdit: isPfkapContributor,
      }
    case FACTSHEET_WORKFLOW_STATUS.APPLICATION:
      return {
        canView: isPfkapContributor,
        canEdit: isPfkapContributor,
      }
    case FACTSHEET_WORKFLOW_STATUS.REVISION:
      return {
        canView: isPfkapContributor,
        canEdit: isPfkapContributor,
      }

    case FACTSHEET_WORKFLOW_STATUS.ACCEPTED: {
      if (published) {
        return {
          canView: isPfkapContributor,
          canEdit: false,
        }
      } else {
        return {
          canView: false,
          canEdit: false,
        }
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.CANCELED:
    case FACTSHEET_WORKFLOW_STATUS.WITHDRAWN:
      return {
        canView: false,
        canEdit: false,
      }

    default:
      return {
        canView: false,
        canEdit: false,
      }
  }
}

const permissionsForFactsheetAssessment = (
  workflow_status: FACTSHEET_WORKFLOW_STATUS_TYPE | null,
  userIsAssessor: boolean,
  published: boolean,
  roles: Array<USER_ROLES_TYPE>,
): { canDelete: boolean; canView: boolean; canAdd: boolean; canEdit: boolean } => {
  const isFactsheetCoordinator = roles.includes(GLOBAL_USER_ROLES['PD-GFCH_FACTSHEET_COORDINATOR'])
  const isPfkapContributor = hasGFCHContributorRoleForProcess(PROJECT.PF_KAP, roles)

  if (isFactsheetCoordinator) {
    return {
      canView: true,
      canEdit: true,
      canAdd: true,
      canDelete: true,
    }
  }

  switch (workflow_status) {
    case FACTSHEET_WORKFLOW_STATUS.CONCEPT:
      return {
        canView: isPfkapContributor,
        canEdit: isPfkapContributor && userIsAssessor,
        canAdd: false,
        canDelete: false,
      }
    case FACTSHEET_WORKFLOW_STATUS.APPLICATION:
      return {
        canView: isPfkapContributor,
        canEdit: isPfkapContributor && userIsAssessor,
        canAdd: false,
        canDelete: false,
      }
    case FACTSHEET_WORKFLOW_STATUS.REVISION:
      return {
        canView: isPfkapContributor,
        canEdit: isPfkapContributor && userIsAssessor,
        canAdd: false,
        canDelete: false,
      }

    case FACTSHEET_WORKFLOW_STATUS.ACCEPTED: {
      if (published) {
        return {
          canView: isPfkapContributor,
          canEdit: false,
          canAdd: false,
          canDelete: false,
        }
      } else {
        return {
          canView: false,
          canEdit: false,
          canAdd: false,
          canDelete: false,
        }
      }
    }

    case FACTSHEET_WORKFLOW_STATUS.CANCELED:
    case FACTSHEET_WORKFLOW_STATUS.WITHDRAWN:
      return {
        canView: false,
        canEdit: false,
        canAdd: false,
        canDelete: false,
      }

    default:
      return {
        canView: false,
        canEdit: false,
        canAdd: false,
        canDelete: false,
      }
  }
}

/**
 * Milestone permissions.
 */
const permissionsForMilestones = (
  roles: Array<USER_ROLES_TYPE>,
  projectUserRoles: Array<valueof<PROJECT_USER_ROLE_TYPE>>,
  userIsProjectExpert: boolean,
  status: DOSSIER_STATUS_TYPE,
  process: PROJECT_TYPE,
  milestoneStatus: MILESTONE_STATUS_TYPE,
  milestoneResponsibleType: MILESTONE_RESPONSIBLE_TYPE_TYPE,
): {
  canView: boolean
  canEdit: boolean
  canEditDueDate: boolean
  canModifyGoals: boolean
} => {
  const hasCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)
  const canEditDueDate = MILESTONE_STATUS.DONE === milestoneStatus ? false : hasCoordinatorRole
  switch (status) {
    case DOSSIER_STATUS.IMPLEMENTATION: {
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasPerProjectEditableRole = hasExternalEditableRole(projectUserRoles)
      const hasPerProjectAdminRole = hasExternalAdminRole(projectUserRoles)
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0

      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(process, roles)

      if (milestoneStatus === MILESTONE_STATUS.DONE) {
        return {
          canView:
            hasGFCHRoleForProcess ||
            hasAnyRoleOnTheProject ||
            isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
          canEdit: hasCoordinatorRole,
          canModifyGoals: hasCoordinatorRole,
          canEditDueDate: canEditDueDate,
        }
      }

      let canEdit = false
      let canModifyGoals = false
      if (milestoneResponsibleType === MILESTONE_RESPONSIBLE_TYPE.GFCH) {
        canEdit = hasGFCHEditableRole
        canModifyGoals = hasGFCHEditableRole
      } else if (milestoneResponsibleType === MILESTONE_RESPONSIBLE_TYPE.PROJECT_ORGANIZATION) {
        canEdit = hasCoordinatorRole || hasPerProjectEditableRole
        canModifyGoals = hasCoordinatorRole || hasPerProjectEditableRole
      }

      return {
        canView:
          hasGFCHRoleForProcess ||
          hasAnyRoleOnTheProject ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEdit: canEdit,
        canModifyGoals: canModifyGoals,
        canEditDueDate: canEditDueDate,
      }
    }

    case DOSSIER_STATUS.CANCELED:
    case DOSSIER_STATUS.FINISHED: {
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(process, roles)
      const hasAnyRoleOnTheProject = projectUserRoles.length > 0
      const hasGFCHCoordinatorRole = hasGFCHCoordinatorRoleForProcess(process, roles)
      return {
        canView:
          hasGFCHRoleForProcess ||
          hasAnyRoleOnTheProject ||
          isProjectExpertEvaluationRoleForProcess(process, userIsProjectExpert),
        canEdit: hasGFCHCoordinatorRole,
        canModifyGoals: hasGFCHCoordinatorRole,
        canEditDueDate: canEditDueDate,
      }
    }

    default:
      return {
        canView: false,
        canEdit: false,
        canEditDueDate: false,
        canModifyGoals: false,
      }
  }
}

const permissionsForKapMilestones = (
  roles: Array<USER_ROLES_TYPE>,
  status: DOSSIER_STATUS_TYPE,
  milestoneStatus: MILESTONE_STATUS_TYPE,
  milestoneResponsibleType: MILESTONE_RESPONSIBLE_TYPE_TYPE,
  isOwnCanton: boolean,
): {
  canView: boolean
  canEdit: boolean
  canEditDueDate: boolean
  canModifyGoals: boolean
} => {
  const hasCoordinatorRole = hasGFCHCoordinatorRoleForProcess(PROJECT.KAP, roles)
  const canEditDueDate = MILESTONE_STATUS.DONE === milestoneStatus ? false : hasCoordinatorRole

  switch (status) {
    case 'APPLICATION': {
      const hasPerProgramViewRole = hasCantonViewRole(roles)
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(PROJECT.KAP, roles)

      const hasPerProgramEditableRole = hasCantonEditableRole(roles)
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(PROJECT.KAP, roles)
      const isCantonResponsible = milestoneResponsibleType === 'PROJECT_ORGANIZATION'

      if (milestoneStatus === MILESTONE_STATUS.DONE) {
        return {
          canView: hasGFCHRoleForProcess || (isOwnCanton && hasPerProgramViewRole),
          canEdit: hasCoordinatorRole,
          canModifyGoals: hasCoordinatorRole,
          canEditDueDate: canEditDueDate,
        }
      }

      return {
        canEdit:
          hasCoordinatorRole ||
          (!isCantonResponsible && hasGFCHEditableRole) ||
          (isCantonResponsible && isOwnCanton && hasPerProgramEditableRole),
        canView: hasGFCHRoleForProcess || (isOwnCanton && hasPerProgramViewRole),
        canModifyGoals:
          hasCoordinatorRole ||
          (!isCantonResponsible && hasGFCHEditableRole) ||
          (isCantonResponsible && isOwnCanton && hasPerProgramEditableRole),
        canEditDueDate: canEditDueDate,
      }
    }
    case 'IMPLEMENTATION': {
      const hasPerProgramViewRole = hasCantonViewRole(roles)
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(PROJECT.KAP, roles)

      const hasPerProgramEditableRole = hasCantonEditableRole(roles)
      const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(PROJECT.KAP, roles)
      const isCantonResponsible = milestoneResponsibleType === 'PROJECT_ORGANIZATION'

      if (milestoneStatus === MILESTONE_STATUS.DONE) {
        return {
          canView: hasGFCHRoleForProcess || (isOwnCanton && hasPerProgramViewRole),
          canEdit: hasCoordinatorRole,
          canModifyGoals: hasCoordinatorRole,
          canEditDueDate: canEditDueDate,
        }
      }

      return {
        canEdit:
          hasCoordinatorRole ||
          (!isCantonResponsible && hasGFCHEditableRole) ||
          (isCantonResponsible && isOwnCanton && hasPerProgramEditableRole),
        canView: hasGFCHRoleForProcess || (isOwnCanton && hasPerProgramViewRole),
        canModifyGoals:
          hasCoordinatorRole ||
          (!isCantonResponsible && hasGFCHEditableRole) ||
          (isCantonResponsible && isOwnCanton && hasPerProgramEditableRole),
        canEditDueDate: canEditDueDate,
      }
    }

    case 'CANCELED':
    case 'FINISHED': {
      const hasPerProgramViewRole = hasCantonViewRole(roles)
      const hasGFCHRoleForProcess = hasGFCHViewRoleForProcess(PROJECT.KAP, roles)

      return {
        canEdit: hasCoordinatorRole,
        canView: hasGFCHRoleForProcess || (isOwnCanton && hasPerProgramViewRole),
        canModifyGoals: hasCoordinatorRole,
        canEditDueDate: canEditDueDate,
      }
    }

    default:
      return {
        canEdit: false,
        canView: false,
        canModifyGoals: false,
        canEditDueDate: false,
      }
  }
}

const permissionToViewNavLinkForReporting = (
  userRoles: Array<USER_ROLES_TYPE>,
): {
  canViewReporting: boolean
  canViewPfKapReporting: boolean
  canViewPfPgvReporting: boolean
  canViewKapReporting: boolean
} => {
  const hasPfKapReportingAccess = hasGFCHViewRoleForProcess(PROJECT.PF_KAP, userRoles)
  const hasPfPgvReportingAccess =
    hasGFCHViewRoleForProcess(PROJECT.PF_PGV, userRoles) ||
    hasExternalExpertEvaluationRoleForProcess(PROJECT.PF_PGV, userRoles)
  const hasKapReportingAccess = hasGFCHViewRoleForProcess(PROJECT.KAP, userRoles) || hasCantonViewRole(userRoles)
  const hasReportingAccess = hasPfKapReportingAccess || hasPfPgvReportingAccess || hasKapReportingAccess

  return {
    canViewReporting: hasReportingAccess,
    canViewPfKapReporting: hasPfKapReportingAccess,
    canViewPfPgvReporting: hasPfPgvReportingAccess,
    canViewKapReporting: hasKapReportingAccess,
  }
}

const permissionsForFinalMilestoneInternalAssessment = (
  roles: Array<USER_ROLES_TYPE>,
  process: PROJECT_TYPE,
): { canEditInternalAssessment: boolean } => {
  const hasGFCHEditableRole = hasGFCHEditableRoleForProcess(process, roles)

  return {
    canEditInternalAssessment: hasGFCHEditableRole,
  }
}

/**
 * Success factors permissions.
 */
const permissionsForSuccessFactors = (globalRoles: Array<USER_ROLES_TYPE>): { canView: boolean; canEdit: boolean } => {
  const canView = [
    GLOBAL_USER_ROLES['PD-SUCCESS_FACTOR_CONTRIBUTOR'],
    GLOBAL_USER_ROLES['PD-GFCH_SUCCESS_FACTOR_COORDINATOR'],
  ].some((role) => globalRoles.includes(role))

  const canEdit = globalRoles.includes(GLOBAL_USER_ROLES['PD-SUCCESS_FACTOR_CONTRIBUTOR'])

  return {
    canView: canView,
    canEdit: canEdit,
  }
}

const permissionToDownloadKapFinancialsReport = (globalRoles: Array<USER_ROLES_TYPE>): boolean => {
  return hasGFCHViewRoleForProcess(PROJECT.KAP, globalRoles)
}

/**
 * Kap Program Journal permissions
 */
const permissionsForKapProgramJournals = (
  globalRoles: Array<USER_ROLES_TYPE>,
): {
  canView: boolean
  canEdit: boolean
} => {
  return {
    canView: hasGFCHViewRoleForProcess(PROJECT.KAP, globalRoles),
    canEdit: hasGFCHEditableRoleForProcess(PROJECT.KAP, globalRoles),
  }
}

/**
 * Project Journal permissions
 */
const permissionsForProjectJournals = (globalRoles: Array<USER_ROLES_TYPE>, projectType: PROJECT_TYPE) => {
  return {
    canView: hasGFCHViewRoleForProcess(projectType, globalRoles),
    canEdit: hasGFCHEditableRoleForProcess(projectType, globalRoles),
  }
}

export const PermissionService = {
  permissionsForProjectDescription,
  permissionsForProjectApplication,
  permissionsForProjectAssessment,
  permissionsForProjectCommittees,
  permissionToCreateProject,
  permissionToViewNavLink,
  permissionToAdministrateUsers,
  permissionToAdministrateFundingRounds,
  permissionToFactsheets,
  permissionsForFactsheetAssessment,
  permissionToFactsheetRelatedProjects,
  permissionToFactsheetDetailsSummary,
  permissionToFactsheetDetails,
  permissionsForFactsheetUsers,
  permissionsForFactsheetApplicationDetails,
  permissionsForFactsheetCommittees,
  permissionsForProjectImplementation,
  permissionsForMilestones,
  permissionsForKapProgramDescription,
  permissionToCreateKapProgram,
  permissionsForKapMilestones,
  permissionToViewAdministrationLink,
  permissionToViewNavLinkForReporting,
  permissionsForSuccessFactors,
  permissionToDownloadKapFinancialsReport,
  permissionsForFinalMilestoneInternalAssessment,
  permissionToEditProjectMilestoneConditions,
  permissionsForKapProgramJournals,
  permissionsForProjectJournals,
}
